import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History, Plus, ShieldAlert, Trash2, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview, DocHeader, Grid } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, dmyTime, titleize, today } from "@/lib/format";
import { SupplierForm, emptySupplier } from "./SupplierForm";

const emptyLine = () => ({ medicine_id: "", batch_no: "", expiry_date: "", quantity: "",
  free_quantity: "0", purchase_price: "", mrp: "", selling_price: "", gst_percent: "12",
  discount_percent: "0", purchase_unit: "", mrp_unit: "", selling_unit: "" });

const emptyForm = () => ({ supplier_id: "", invoice_no: "", supplier_invoice_date: today(),
  purchase_date: today(), due_date: "", payment_date: "", payment_terms_days: "0",
  reference: "", notes: "", payment_note: "", interstate: false,
  payments: [{ mode: "bank_transfer", amount: "", reference: "", note: "" }],
  items: [emptyLine()] });

const PAY_MODES = ["cash", "upi", "card", "bank_transfer", "cheque", "credit", "other"];

const num = (v) => Number(v || 0);
const lineMaths = (l) => {
  const gross = num(l.quantity) * num(l.purchase_price);
  const discount = gross * num(l.discount_percent) / 100;
  const taxable = gross - discount;
  const gst = taxable * num(l.gst_percent) / 100;
  return { gross, discount, taxable, gst, total: taxable + gst };
};

const toEditForm = (p) => ({
  ...emptyForm(),
  supplier_id: p.supplier_id, invoice_no: p.supplier_invoice_no || "",
  supplier_invoice_date: (p.supplier_invoice_date || "").slice(0, 10),
  purchase_date: (p.purchase_date || p.created_at || "").slice(0, 10),
  due_date: (p.due_date || "").slice(0, 10),
  payment_date: (p.payment_date || "").slice(0, 10),
  payment_terms_days: String(p.payment_terms_days ?? 0),
  payment_note: p.payment_note || "", interstate: Boolean(p.interstate),
  payments: (p.payments || []).length
    ? p.payments.map((x) => ({ mode: x.mode, amount: String(x.amount), reference: x.reference || "",
        note: x.note || "" }))
    : [{ mode: "bank_transfer", amount: "", reference: "", note: "" }],
  reference: p.reference || "", notes: p.notes || "",
  items: (p.items || []).map((l) => ({
    medicine_id: l.medicine_id, batch_no: l.batch_no || "",
    expiry_date: (l.expiry_date || "").slice(0, 10), quantity: String(l.quantity ?? ""),
    free_quantity: String(l.free_quantity ?? 0), purchase_price: String(l.purchase_price ?? ""),
    mrp: String(l.mrp ?? ""), selling_price: String(l.selling_price ?? ""),
    gst_percent: String(l.gst_percent ?? 12), discount_percent: String(l.discount_percent ?? 0),
    purchase_unit: l.purchase_unit || "", mrp_unit: l.mrp_unit || "",
    selling_unit: l.selling_unit || "",
  })),
});

export default function Purchases({ embedded = false }) {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [print, setPrint] = useState(null);
  const [supOpen, setSupOpen] = useState(false);
  const [supForm, setSupForm] = useState(emptySupplier());
  const [supError, setSupError] = useState("");
  const [ret, setRet] = useState(null);
  const [retQty, setRetQty] = useState({});
  const [retReason, setRetReason] = useState("");
  const [medQuery, setMedQuery] = useState("");
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [masterResults, setMasterResults] = useState([]);
  const [business, setBusiness] = useState(null);
  const [invoiceWarning, setInvoiceWarning] = useState("");
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [finalising, setFinalising] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", mode: "bank_transfer", reference: "", note: "" });
  const [correctFor, setCorrectFor] = useState(null);
  const [correctReason, setCorrectReason] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [deletedItems, setDeletedItems] = useState(null);
  const [deletedError, setDeletedError] = useState("");
  const [hardDeleteFor, setHardDeleteFor] = useState(null);
  const [hardDeleteConfirmation, setHardDeleteConfirmation] = useState("");
  const [hardDeleteBusy, setHardDeleteBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/pharmacy/purchases");
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [setItems, setError]);

  const loadSuppliers = useCallback(() => {
    api.get("/pharmacy/suppliers", { params: { status: "active" } })
      .then(({ data }) => setSuppliers(data.items)).catch(() => {});
  }, [setSuppliers]);

  const loadProducts = useCallback((supplierId) => {
    api.get("/pharmacy/purchase-context", { params: { supplier_id: supplierId || undefined } })
      .then(({ data }) => setProducts(data.products)).catch(() => {});
  }, [setProducts]);

  useEffect(() => {
    if (!can("pharmacy.inventory.view")) return;
    load(); loadSuppliers(); loadProducts("");
    api.get("/settings/full").then(({ data }) => setBusiness(data.business || null)).catch(() => {});
  }, [load, loadSuppliers, loadProducts, can]);

  useEffect(() => {
    if (form.supplier_id) loadProducts(form.supplier_id);
  }, [form.supplier_id, loadProducts]);

  // Warn as soon as this supplier + invoice number combination already exists.
  useEffect(() => {
    setInvoiceWarning(""); setOverride(false); setOverrideReason("");
    if (!form.supplier_id || !form.invoice_no.trim()) return;
    const t = setTimeout(() => {
      api.get("/pharmacy/purchases/invoice-check",
        { params: { supplier_id: form.supplier_id, invoice_no: form.invoice_no.trim(),
          exclude_purchase_id: editingId || undefined } })
        .then(({ data }) => {
          if (data.duplicate && data.purchase?.purchase_no !== undefined)
            setInvoiceWarning(`Supplier invoice ${form.invoice_no.trim()} is already recorded as ${data.purchase.purchase_no}.`);
        }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [form.supplier_id, form.invoice_no, editingId]);

  const totals = useMemo(() => {
    const rows = form.items.map(lineMaths);
    const taxable = rows.reduce((s, r) => s + r.taxable, 0);
    const gst = rows.reduce((s, r) => s + r.gst, 0);
    const discount = rows.reduce((s, r) => s + r.discount, 0);
    const net = taxable + gst;
    const grand = Math.round(net);
    return { taxable, gst, discount, net, roundOff: grand - net, grand };
  }, [form.items]);

  if (!can("pharmacy.inventory.view")) return <NoAccess what="purchases" />;

  const setLine = (i, patch) =>
    setForm({ ...form, items: form.items.map((l, k) => (k === i ? { ...l, ...patch } : l)) });

  const pickProduct = (i, medicine_id) => {
    const p = products.find((x) => x.medicine_id === medicine_id);
    const last = p?.last;
    setLine(i, {
      medicine_id,
      gst_percent: String(last?.gst_percent ?? p?.gst_percent ?? 12),
      purchase_price: last ? String(last.purchase_price) : (p?.purchase_price ? String(p.purchase_price) : ""),
      mrp: last ? String(last.mrp) : (p?.mrp ? String(p.mrp) : ""),
      selling_price: last ? String(last.selling_price) : (p?.selling_price ? String(p.selling_price) : ""),
      purchase_unit: p?.packaging?.[0]?.code || "",
      mrp_unit: p?.packaging?.[0]?.code || "",
      selling_unit: p?.packaging?.[0]?.code || "",
    });
  };

  const openNew = () => {
    setEditingId(null); setForm(emptyForm()); setFormError(""); setShowAllProducts(false); setCorrectFor(null); setCorrectReason(""); setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.purchase_id); setForm(toEditForm(p)); setFormError("");
    setShowAllProducts(true); setOpen(true);
  };

  const submit = async (status, printAfter = false) => {
    setFormError("");
    if (!form.supplier_id) { setFormError("Select a supplier."); return; }
    const lines = form.items.filter((l) => l.medicine_id && num(l.quantity) > 0);
    if (!lines.length) { setFormError("Add at least one product with a quantity."); return; }
    for (const l of lines) {
      if (!l.batch_no.trim()) { setFormError("Every product needs a batch number."); return; }
      if (!l.expiry_date) { setFormError("Every product needs an expiry date."); return; }
      if (num(l.mrp) <= 0) { setFormError("MRP must be greater than zero."); return; }
    }
    if (invoiceWarning && !override) {
      setFormError("This supplier invoice number is already used. Tick the override box and give a reason to continue.");
      return;
    }
    setBusy(true);
    const body = {
      supplier_id: form.supplier_id, invoice_no: form.invoice_no,
      supplier_invoice_date: form.supplier_invoice_date, purchase_date: form.purchase_date,
      due_date: form.due_date, reference: form.reference, notes: form.notes, status,
      payment_date: form.payment_date, payment_terms_days: Number(form.payment_terms_days || 0),
      payment_note: form.payment_note, interstate: form.interstate,
      payments: form.payments.filter((p) => num(p.amount) > 0)
        .map((p) => ({ mode: p.mode, amount: num(p.amount), reference: p.reference, note: p.note })),
      payment_mode: form.payments[0]?.mode || "bank_transfer",
      override_duplicate_invoice: override, override_reason: overrideReason,
      items: lines.map((l) => ({
        medicine_id: l.medicine_id, batch_no: l.batch_no.trim(), expiry_date: l.expiry_date,
        quantity: Number(l.quantity), free_quantity: Number(l.free_quantity || 0),
        purchase_price: num(l.purchase_price), mrp: num(l.mrp),
        selling_price: num(l.selling_price) || num(l.mrp),
        gst_percent: num(l.gst_percent), discount_percent: num(l.discount_percent),
        purchase_unit: l.purchase_unit, mrp_unit: l.mrp_unit, selling_unit: l.selling_unit })),
    };
    try {
      const { data } = editingId
        ? await api.patch(`/pharmacy/purchases/${editingId}`, body)
        : await api.post("/pharmacy/purchases", body);
      toast.success(status === "draft"
        ? `Draft ${data.purchase.purchase_no} saved — no stock was added.`
        : `Purchase ${data.purchase.purchase_no} recorded and stock updated.`);
      setOpen(false); setEditingId(null); setForm(emptyForm()); load(); loadSuppliers();
      if (printAfter) setPrint(data.purchase);
    } catch (e) { setFormError(apiError(e)); toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const finalise = async (p) => {
    setFinalising(p.purchase_id);
    try {
      const { data } = await api.post(`/pharmacy/purchases/${p.purchase_id}/finalise`);
      toast.success(`${data.purchase.purchase_no} finalised — stock, batch and expiry updated.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setFinalising(null); }
  };

  const loadDeleted = async () => {
    setDeletedError("");
    try {
      const { data } = await api.get("/pharmacy/purchases/deleted");
      setDeletedItems(data.items || []);
    } catch (err) {
      setDeletedError(apiError(err));
    }
  };

  const openDeleted = () => {
    setDeletedOpen(true);
    setDeletedItems(null);
    loadDeleted();
  };

  const submitDelete = async (event) => {
    event.preventDefault();
    if (deleteReason.trim().length < 3) {
      toast.error("A deletion reason of at least 3 characters is required.");
      return;
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.post(`/pharmacy/purchases/${deleteFor.purchase_id}/delete`, {
        reason: deleteReason.trim(),
      });
      toast.success(`${data.purchase_no} deleted. Its unused stock was reversed.`);
      setDeleteFor(null);
      setDeleteReason("");
      load();
      if (deletedOpen) {
        loadDeleted();
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const submitHardDelete = async (event) => {
    event.preventDefault();
    if (hardDeleteConfirmation !== "DELETE") {
      toast.error("Type DELETE exactly to confirm the permanent deletion.");
      return;
    }
    setHardDeleteBusy(true);
    try {
      const { data } = await api.post(
        `/pharmacy/purchases/${hardDeleteFor.purchase_id}/hard-delete`,
        { confirmation: hardDeleteConfirmation },
      );
      toast.success(`${data.purchase_no} was permanently removed from Pharmacy records.`);
      setHardDeleteFor(null);
      setHardDeleteConfirmation("");
      load();
      if (deletedOpen) {
        loadDeleted();
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setHardDeleteBusy(false);
    }
  };

  const submitPharmacyReset = async (event) => {
    event.preventDefault();
    if (resetConfirmation !== "RESET PHARMACY") {
      toast.error("Type RESET PHARMACY exactly to confirm the reset.");
      return;
    }
    if (resetReason.trim().length < 3) {
      toast.error("A reset reason of at least 3 characters is required.");
      return;
    }
    setResetBusy(true);
    try {
      await api.post("/pharmacy/maintenance/reset-data", {
        confirmation: resetConfirmation,
        reason: resetReason.trim(),
      });
      toast.success("Pharmacy transactional data was permanently reset.");
      setResetOpen(false);
      setResetConfirmation("");
      setResetReason("");
      setDeletedItems([]);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResetBusy(false);
    }
  };

  const openDeletedDetail = async (purchase) => {
    try {
      const { data } = await api.get(`/pharmacy/purchases/deleted/${purchase.purchase_id}`);
      setDeletedOpen(false);
      setDetail(data.purchase);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pharmacy/purchases/${payFor.purchase_id}/payments`, {
        amount: num(payForm.amount), mode: payForm.mode, reference: payForm.reference,
        note: payForm.note });
      toast.success("Payment recorded against this purchase.");
      setPayFor(null); setPayForm({ amount: "", mode: "bank_transfer", reference: "", note: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    const lines = form.items.filter((l) => l.medicine_id && num(l.quantity) > 0);
    try {
      await api.post(`/pharmacy/purchases/${correctFor.purchase_id}/correct`, {
        reason: correctReason, interstate: form.interstate,
        items: lines.map((l) => ({ medicine_id: l.medicine_id, batch_no: l.batch_no.trim(),
          expiry_date: l.expiry_date, quantity: Number(l.quantity),
          free_quantity: Number(l.free_quantity || 0), purchase_price: num(l.purchase_price),
          mrp: num(l.mrp), selling_price: num(l.selling_price) || num(l.mrp),
          gst_percent: num(l.gst_percent), discount_percent: num(l.discount_percent) })) });
      toast.success("Purchase corrected — stock and batches were rebuilt.");
      setCorrectFor(null); setCorrectReason(""); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addSupplier = async (e) => {
    e.preventDefault(); setSupError("");
    try {
      const { data } = await api.post("/pharmacy/suppliers", {
        ...supForm, payment_terms_days: Number(supForm.payment_terms_days || 0),
        credit_limit: Number(supForm.credit_limit || 0),
      });
      toast.success("Supplier added.");
      (data.warnings || []).forEach((w) => toast.warning(w));
      setSuppliers((s) => [...s, data.supplier]);
      setForm((f) => ({ ...f, supplier_id: data.supplier.supplier_id }));
      setSupOpen(false); setSupForm(emptySupplier());
    } catch (err) { setSupError(apiError(err)); }
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    const lines = Object.entries(retQty).filter(([, q]) => Number(q) > 0)
      .map(([batch_id, q]) => ({ batch_id, quantity: Number(q) }));
    if (!lines.length) { toast.error("Enter a quantity to return."); return; }
    try {
      await api.post("/pharmacy/purchase-returns", { purchase_id: ret.purchase_id, items: lines, reason: retReason });
      toast.success("Purchase return recorded and stock adjusted.");
      setRet(null); setRetQty({}); setRetReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const q = medQuery.trim().toLowerCase();
  const matches = products.filter((m) => !q || m.name.toLowerCase().includes(q) || (m.barcode || "").includes(q));
  const linkedFirst = [...matches.filter((m) => m.linked), ...matches.filter((m) => !m.linked)];
  const medOptions = form.supplier_id && !showAllProducts && matches.some((m) => m.linked)
    ? matches.filter((m) => m.linked)
    : linkedFirst;

  if (error) return <ErrorState message={error} />;

  return (
    <div data-testid="purchases-page">
      {embedded ? (
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={openDeleted}
            className={ghostBtnCls}
            data-testid="deleted-purchases-btn"
          >
            <History className="h-4 w-4" /> Deleted purchases
          </button>
          {isAdmin && can("pharmacy.data.reset") && (
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
              data-testid="reset-pharmacy-data-btn"
            >
              <ShieldAlert className="h-4 w-4" /> Reset pharmacy data
            </button>
          )}
          {can("pharmacy.purchase.create") && (
            <div>
              <button
                onClick={openNew}
                className={primaryBtnCls}
                data-testid="add-purchase-btn"
              >
                New purchase
              </button>
            </div>
          )}
        </div>
      ) : (
        <PageHeader overline="Pharmacy" title="Purchase entry"
          subtitle="Record supplier purchases with batch and expiry. Stock is only created when a purchase is finalised — drafts never touch inventory."
          actions={(
            <>
              <button
                onClick={openDeleted}
                className={ghostBtnCls}
                data-testid="deleted-purchases-btn"
              >
                <History className="h-4 w-4" /> Deleted purchases
              </button>
              {isAdmin && can("pharmacy.data.reset") && (
                <button
                  type="button"
                  onClick={() => setResetOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                  data-testid="reset-pharmacy-data-btn"
                >
                  <ShieldAlert className="h-4 w-4" /> Reset pharmacy data
                </button>
              )}
              {can("pharmacy.purchase.create") && (
                <button
                  onClick={openNew}
                  className={primaryBtnCls}
                  data-testid="add-purchase-btn"
                >
                  New purchase
                </button>
              )}
            </>
          )}
        />
      )}

      {!items ? <Loading /> : (
        <Table testid="purchases-table"
          columns={["Purchase no.", "Supplier", "Supplier invoice", "Date", "Items", "Total", "Status", ""]}
          rows={items} empty={<EmptyState title="No purchases yet" subtitle="Record a purchase to bring stock in with batch and expiry." testid="purchases-empty" />}
          renderRow={(p) => (
            <tr key={p.purchase_id} data-testid={`purchase-row-${p.purchase_id}`}>
              <Td className="font-mono-num text-slate-900">{p.purchase_no}</Td>
              <Td className="font-medium text-slate-900">{p.supplier_name}</Td>
              <Td className="text-slate-600">{p.supplier_invoice_no || "—"}</Td>
              <Td className="text-slate-500 whitespace-nowrap">{dmy(p.purchase_date || p.created_at)}</Td>
              <Td className="text-slate-600">{(p.items || []).length}</Td>
              <Td className="text-slate-900">{inr(p.total)}</Td>
              <Td><StatusPill status={p.status === "draft" ? "draft" : "completed"} /></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setDetail(p)} className={ghostBtnCls} data-testid={`view-purchase-${p.purchase_id}`}>View</button>
                  <button onClick={() => setPrint(p)} className={ghostBtnCls} data-testid={`print-purchase-${p.purchase_id}`}>
                    <Printer className="h-3.5 w-3.5" /> Print
                  </button>
                  {p.status === "draft" && can("pharmacy.purchase.create") && (
                    <>
                      <button onClick={() => openEdit(p)} className={ghostBtnCls} data-testid={`edit-purchase-${p.purchase_id}`}>Edit draft</button>
                      <button onClick={() => finalise(p)} disabled={finalising === p.purchase_id}
                        className={primaryBtnCls} data-testid={`finalise-purchase-${p.purchase_id}`}>
                        {finalising === p.purchase_id ? "Finalising…" : "Finalise"}
                      </button>
                    </>
                  )}
                  {p.status !== "draft" && can("pharmacy.purchase.return") && (
                    <button onClick={() => { setRet(p); setRetQty({}); setRetReason(""); }}
                      className={ghostBtnCls} data-testid={`return-purchase-${p.purchase_id}`}>Return</button>
                  )}
                  {p.status !== "draft" && can("pharmacy.supplier.payments") && (p.balance_amount ?? p.total) > 0 && (
                    <button onClick={() => { setPayFor(p); setPayForm({ ...payForm,
                      amount: String(p.balance_amount ?? p.total) }); }}
                      className={ghostBtnCls} data-testid={`pay-purchase-${p.purchase_id}`}>
                      Add payment
                    </button>
                  )}
                  {p.status !== "draft" && isAdmin && can("pharmacy.purchase.create") && (
                    <button onClick={() => { setCorrectFor(p); setForm(toEditForm(p));
                      setEditingId(null); setCorrectReason(""); setShowAllProducts(true); setOpen(true); }}
                      className={ghostBtnCls} data-testid={`correct-purchase-${p.purchase_id}`}>
                      Correct
                    </button>
                  )}
                  {can("pharmacy.purchase.delete") && (
                    <button
                      type="button"
                      onClick={() => { setDeleteFor(p); setDeleteReason(""); }}
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                      data-testid={`delete-purchase-${p.purchase_id}`}
                    >
                      Delete
                    </button>
                  )}
                  {isAdmin && can("pharmacy.purchase.hard_delete") && (
                    <button
                      type="button"
                      onClick={() => {
                        setHardDeleteFor(p);
                        setHardDeleteConfirmation("");
                      }}
                      className="text-xs font-semibold text-red-800 underline decoration-red-300 underline-offset-4 hover:text-red-950"
                      data-testid={`hard-delete-purchase-${p.purchase_id}`}
                    >
                      Hard delete
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => { setOpen(false); setEditingId(null); }} wide
        title={editingId ? "Edit draft purchase" : "Purchase order / entry"}
        subtitle="Totals are always recalculated and stored by the server. Finalising updates stock, batch and expiry."
        testid="add-purchase-modal">
        <div className="space-y-7">
          <div>
            <p className="overline text-[#EA580C] mb-4">Supplier &amp; document</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Supplier *">
                <div className="flex gap-2">
                  <select className={inputCls} value={form.supplier_id} data-testid="purchase-supplier-select"
                    onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                    <option value="">Select supplier</option>
                    {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                  </select>
                  {can("pharmacy.suppliers.manage") && (
                    <button onClick={() => { setSupForm(emptySupplier()); setSupError(""); setSupOpen(true); }}
                      className={ghostBtnCls} data-testid="add-supplier-btn">
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Field>
              <Field label="PO / purchase no.">
                <input className={inputCls} readOnly data-testid="purchase-no-field"
                  value={editingId ? (items?.find((x) => x.purchase_id === editingId)?.purchase_no || "") : "Auto-generated on save"} />
              </Field>
              <Field label="Supplier invoice no.">
                <input className={inputCls} value={form.invoice_no} data-testid="purchase-invoice-no-input"
                  onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
              </Field>
              <Field label="Supplier invoice date">
                <input type="date" className={inputCls} value={form.supplier_invoice_date}
                  data-testid="purchase-supplier-date-input"
                  onChange={(e) => setForm({ ...form, supplier_invoice_date: e.target.value })} />
              </Field>
              <Field label="Purchase date">
                <input type="date" className={inputCls} value={form.purchase_date} data-testid="purchase-date-input"
                  onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
              </Field>
              <Field label="Due date">
                <input type="date" className={inputCls} value={form.due_date} data-testid="purchase-due-date-input"
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </Field>
              <Field label="Reference / notes" className="sm:col-span-3">
                <input className={inputCls} value={form.reference} data-testid="purchase-reference-input"
                  placeholder="Transport, PO reference, remarks"
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
            </div>
            {invoiceWarning && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="duplicate-invoice-warning">
                <p className="text-sm font-medium text-amber-900">{invoiceWarning}</p>
                {isAdmin ? (
                  <div className="mt-3 space-y-3">
                    <label className="flex items-center gap-2 text-sm text-amber-900">
                      <input type="checkbox" checked={override} data-testid="override-invoice-checkbox"
                        onChange={(e) => setOverride(e.target.checked)} />
                      Override — this repeated invoice number is genuine
                    </label>
                    {override && (
                      <input className={inputCls} value={overrideReason} data-testid="override-reason-input"
                        placeholder="Reason for the override (required, min 5 characters)"
                        onChange={(e) => setOverrideReason(e.target.value)} />
                    )}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-amber-800">Only an Admin or Owner can override a duplicate supplier invoice number.</p>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="overline text-[#EA580C]">Products</p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={showAllProducts} data-testid="show-all-products-toggle"
                    onChange={(e) => setShowAllProducts(e.target.checked)} />
                  Show all products
                </label>
                <input className={`${inputCls} !w-56`} value={medQuery} data-testid="purchase-product-search"
                  placeholder="Filter by name or barcode" data-enter-next="false"
                  onChange={(e) => setMedQuery(e.target.value)} />
                <button onClick={async () => {
                    if (!medQuery.trim()) { toast.error('Enter a search term first.'); return; }
                    try {
                      const { data } = await api.get('/pharmacy/medicines/master/search', { params: { q: medQuery.trim() } });
                      setMasterResults(data.items || []);
                    } catch (err) { toast.error(apiError(err) || 'Could not search master catalog.'); }
                  }} className={ghostBtnCls} data-testid="purchase-search-master-btn">
                  Search master catalog
                </button>
                <button onClick={() => setForm({ ...form, items: [...form.items, emptyLine()] })}
                  className={ghostBtnCls} data-testid="add-purchase-line-btn">
                  <Plus className="h-3.5 w-3.5" /> Add to purchase
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {masterResults.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                  <p className="font-medium mb-2">Master catalog results</p>
                  <div className="space-y-2">
                    {masterResults.map((m) => (
                      <div key={m.master_id} className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{m.name}</div>
                          <div className="text-xs text-slate-600">{m.generic_name || ''}</div>
                        </div>
                        <div>
                          <button className={ghostBtnCls} onClick={async () => {
                            try {
                              const { data } = await api.post('/pharmacy/medicines/import-master', { master_id: m.master_id });
                              toast.success(`${data.medicine.name} imported to your business.`);
                              // Refresh product list for current supplier
                              await loadProducts(form.supplier_id);
                              setMasterResults([]);
                            } catch (err) { toast.error(apiError(err) || 'Could not import product.'); }
                          }} data-testid={`purchase-import-master-${m.master_id}`}>
                            Add to business
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {form.items.map((l, i) => {
                const m = lineMaths(l);
                const p = products.find((x) => x.medicine_id === l.medicine_id);
                return (
                  <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid={`purchase-line-${i}`}>
                      <div className="grid gap-3 sm:grid-cols-12">
                        <div className="sm:col-span-4">
                          <Field label="Product *">
                            <select className={inputCls} value={l.medicine_id} data-testid={`purchase-medicine-${i}`}
                              onChange={(e) => pickProduct(i, e.target.value)}>
                              <option value="">Select product</option>
                              {medOptions.map((x) => (
                                <option key={x.medicine_id} value={x.medicine_id}>{x.name}{x.linked ? " ★" : ""}</option>
                              ))}
                            </select>
                          </Field>
                        </div>
                        <div className="sm:col-span-8 grid gap-3 sm:grid-cols-3">
                          {p?.packaging?.length > 0 && (
                            <>
                              <Field label="Purchase as">
                                <select className={inputCls} data-testid={`purchase-unit-${i}`}
                                  value={l.purchase_unit || p.packaging[0].code}
                                  onChange={(e) => setLine(i, { purchase_unit: e.target.value })}>
                                  {p.packaging.map((unit) => (
                                    <option key={unit.code} value={unit.code}>
                                      {unit.label} ({unit.factor} base)
                                    </option>
                                  ))}
                                </select>
                              </Field>
                              <Field label="MRP applies to">
                                <select className={inputCls} data-testid={`purchase-mrp-unit-${i}`}
                                  value={l.mrp_unit || l.purchase_unit || p.packaging[0].code}
                                  onChange={(e) => setLine(i, { mrp_unit: e.target.value })}>
                                  {p.packaging.map((unit) => <option key={unit.code} value={unit.code}>{unit.label}</option>)}
                                </select>
                              </Field>
                              <Field label="Selling price applies to">
                                <select className={inputCls} data-testid={`purchase-selling-unit-${i}`}
                                  value={l.selling_unit || l.mrp_unit || l.purchase_unit || p.packaging[0].code}
                                  onChange={(e) => setLine(i, { selling_unit: e.target.value })}>
                                  {p.packaging.map((unit) => <option key={unit.code} value={unit.code}>{unit.label}</option>)}
                                </select>
                              </Field>
                            </>
                          )}
                          <div>
                            <Field label="Batch no. *">
                              <input className={`${inputCls} sm:!w-44`} value={l.batch_no} data-testid={`purchase-batch-${i}`}
                                onChange={(e) => setLine(i, { batch_no: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Expiry date *">
                              <input type="date" className={`${inputCls} sm:!w-44`} value={l.expiry_date}
                                data-testid={`purchase-expiry-${i}`} onChange={(e) => setLine(i, { expiry_date: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Purchase Qty *">
                              <input type="number" min="1" className={`${inputCls} sm:!w-28`} value={l.quantity}
                                data-testid={`purchase-qty-${i}`} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Free Qty">
                              <input type="number" min="0" className={`${inputCls} sm:!w-28`} value={l.free_quantity}
                                data-testid={`purchase-free-qty-${i}`} onChange={(e) => setLine(i, { free_quantity: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Purchase rate (₹) *">
                              <input type="number" min="0" className={`${inputCls} sm:!w-36`} value={l.purchase_price}
                                data-testid={`purchase-rate-${i}`} onChange={(e) => setLine(i, { purchase_price: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="MRP (₹) *">
                              <input type="number" min="0" className={`${inputCls} sm:!w-36`} value={l.mrp}
                                data-testid={`purchase-mrp-${i}`} onChange={(e) => setLine(i, { mrp: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Selling price (₹)">
                              <input type="number" min="0" className={inputCls} value={l.selling_price}
                                data-testid={`purchase-selling-${i}`} onChange={(e) => setLine(i, { selling_price: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="GST %">
                              <input type="number" min="0" className={inputCls} value={l.gst_percent}
                                data-testid={`purchase-gst-${i}`} onChange={(e) => setLine(i, { gst_percent: e.target.value })} />
                            </Field>
                          </div>
                          <div>
                            <Field label="Discount %">
                              <input type="number" min="0" className={inputCls} value={l.discount_percent}
                                data-testid={`purchase-discount-${i}`} onChange={(e) => setLine(i, { discount_percent: e.target.value })} />
                            </Field>
                          </div>
                        </div>
                      </div>
                    {p?.packaging?.length > 0 && (
                      <p className="mt-2 text-xs text-teal-700" data-testid={`purchase-packaging-preview-${i}`}>
                        {(() => {
                          const unit = p.packaging.find((x) => x.code === (l.purchase_unit || p.packaging[0].code));
                          return `${Number(l.quantity || 0)} ${unit?.label || "unit"}(s) = ${Number(l.quantity || 0) * (unit?.factor || 1)} base units`;
                        })()}
                      </p>
                    )}
                    {p?.last && (
                      <p className="mt-2 text-xs text-slate-500" data-testid={`purchase-last-price-${i}`}>
                        Last from this supplier ({p.last.purchase_no || "—"}): rate {inr(p.last.purchase_price)} ·
                        {" "}MRP {inr(p.last.mrp)} · batch {p.last.batch_no || "—"}
                      </p>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Taxable {inr(m.taxable)} · GST {inr(m.gst)} ·
                          <span className="ml-1 font-semibold text-slate-900" data-testid={`purchase-line-total-${i}`}>{inr(m.total)}</span>
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {`Total received: ${Number(l.quantity || 0) + Number(l.free_quantity || 0)} units`}
                          {Number(l.free_quantity || 0) > 0 ? ` (Purchase ${Number(l.quantity || 0)} + Free ${Number(l.free_quantity || 0)})` : ""}
                        </p>
                      </div>
                      {form.items.length > 1 && (
                        <button onClick={() => setForm({ ...form, items: form.items.filter((_, k) => k !== i) })}
                          data-testid={`remove-purchase-line-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-800">
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <p className="overline mb-4 text-[#EA580C]">Payment</p>
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Payment date">
                <input type="date" className={inputCls} value={form.payment_date}
                  data-testid="purchase-payment-date"
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
              </Field>
              <Field label="Payment terms (days)">
                <input type="number" min="0" className={inputCls} value={form.payment_terms_days}
                  data-testid="purchase-payment-terms"
                  onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} />
              </Field>
              <Field label="Payment note" className="sm:col-span-2">
                <input className={inputCls} value={form.payment_note} data-testid="purchase-payment-note"
                  onChange={(e) => setForm({ ...form, payment_note: e.target.value })} />
              </Field>
            </div>
            <div className="mt-4 space-y-3">
              {form.payments.map((p, i) => (
                <div key={i} className="grid gap-3 sm:grid-cols-4" data-testid={`purchase-payment-${i}`}>
                  <select className={inputCls} value={p.mode} data-testid={`purchase-payment-mode-${i}`}
                    onChange={(e) => setForm({ ...form, payments: form.payments.map((x, k) =>
                      (k === i ? { ...x, mode: e.target.value } : x)) })}>
                    {PAY_MODES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
                  </select>
                  <input type="number" min="0" placeholder="Amount" className={inputCls} value={p.amount}
                    data-testid={`purchase-payment-amount-${i}`}
                    onChange={(e) => setForm({ ...form, payments: form.payments.map((x, k) =>
                      (k === i ? { ...x, amount: e.target.value } : x)) })} />
                  <input placeholder="Reference / UTR" className={inputCls} value={p.reference}
                    onChange={(e) => setForm({ ...form, payments: form.payments.map((x, k) =>
                      (k === i ? { ...x, reference: e.target.value } : x)) })} />
                  <div className="flex gap-2">
                    <input placeholder="Note" className={inputCls} value={p.note}
                      onChange={(e) => setForm({ ...form, payments: form.payments.map((x, k) =>
                        (k === i ? { ...x, note: e.target.value } : x)) })} />
                    {form.payments.length > 1 && (
                      <button onClick={() => setForm({ ...form,
                        payments: form.payments.filter((_, k) => k !== i) })}
                        className="text-red-600" data-testid={`remove-payment-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={() => setForm({ ...form, payments: [...form.payments,
                  { mode: "cash", amount: "", reference: "", note: "" }] })}
                  className={ghostBtnCls} data-testid="add-split-payment-btn">
                  <Plus className="h-3.5 w-3.5" /> Split payment
                </button>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={form.interstate} data-testid="purchase-interstate-toggle"
                    onChange={(e) => setForm({ ...form, interstate: e.target.checked })} />
                  Inter-state purchase (IGST instead of CGST + SGST)
                </label>
                <span className="text-sm text-slate-600" data-testid="purchase-paid-summary">
                  Paying {inr(form.payments.reduce((s, p) => s + num(p.amount), 0))} of {inr(totals.grand)}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-900 bg-[#0F172A] p-5 text-white">
            <div className="grid gap-3 sm:grid-cols-5 text-sm">
              {[["Taxable", inr(totals.taxable), "purchase-taxable"], ["Discount", `- ${inr(totals.discount)}`, "purchase-discount-total"],
                [form.interstate ? "IGST" : "CGST + SGST", inr(totals.gst), "purchase-gst-total"],
                ["Round off", inr(totals.roundOff), "purchase-round-off"]].map(([k, v, t]) => (
                <div key={k}><p className="overline text-slate-400">{k}</p><p className="mt-1 font-semibold" data-testid={t}>{v}</p></div>
              ))}
              <div><p className="overline text-[#EA580C]">Grand total</p>
                <p className="mt-1 font-ui text-xl font-bold" data-testid="purchase-grand-total">{inr(totals.grand)}</p></div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Indicative — the server recalculates and stores the authoritative total.</p>
          </div>

          {formError && <p className="text-sm font-medium text-red-600" data-testid="purchase-form-error">{formError}</p>}
          {correctFor ? (
            <form onSubmit={submitCorrection} className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900">
                Correcting finalised purchase <b>{correctFor.purchase_no}</b>. The original batches are
                reversed and rebuilt from these lines. Batches that have already been billed cannot be corrected.
              </p>
              <input required minLength={5} className={inputCls} value={correctReason}
                data-testid="correction-reason-input" placeholder="Reason for the correction (required)"
                onChange={(e) => setCorrectReason(e.target.value)} />
              <button className={primaryBtnCls} data-testid="purchase-correct-submit-btn">
                Apply correction
              </button>
            </form>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button onClick={() => submit("draft")} disabled={busy} className={ghostBtnCls} data-testid="purchase-save-draft-btn">
                {editingId ? "Update draft" : "Save draft"}
              </button>
              <button onClick={() => submit("final")} disabled={busy} className={primaryBtnCls} data-testid="purchase-save-btn">
                Save &amp; update stock
              </button>
              <button onClick={() => submit("final", true)} disabled={busy} className={ghostBtnCls} data-testid="purchase-save-print-btn">
                <Printer className="h-4 w-4" /> Save &amp; print
              </button>
            </div>
          )}
        </div>
      </Modal>

      <Modal open={Boolean(payFor)} onClose={() => setPayFor(null)} title="Record purchase payment"
        subtitle={payFor ? `${payFor.purchase_no} · ${payFor.supplier_name} · balance ${inr(payFor.balance_amount ?? payFor.total)}` : ""}
        testid="purchase-payment-modal">
        <form onSubmit={addPayment} className="grid gap-5 sm:grid-cols-2">
          <Field label="Amount (₹) *">
            <input required type="number" min="0.01" step="0.01" className={inputCls} value={payForm.amount}
              data-testid="purchase-pay-amount-input"
              onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
          </Field>
          <Field label="Mode">
            <select className={inputCls} value={payForm.mode} data-testid="purchase-pay-mode-select"
              onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}>
              {PAY_MODES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Reference / UTR">
            <input className={inputCls} value={payForm.reference}
              onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
          </Field>
          <Field label="Note">
            <input className={inputCls} value={payForm.note}
              onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="purchase-pay-submit-btn">Save payment</button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(deleteFor)}
        onClose={() => setDeleteFor(null)}
        title="Delete purchase"
        subtitle={deleteFor ? `${deleteFor.purchase_no} · ${deleteFor.supplier_name}` : ""}
        testid="delete-purchase-modal"
      >
        {deleteFor && (
          <form onSubmit={submitDelete} className="space-y-5">
            <div className="grid gap-3 rounded-md bg-slate-50 p-4 text-sm sm:grid-cols-2"
              data-testid="delete-purchase-summary">
              <p><span className="text-slate-500">Purchase:</span> {deleteFor.purchase_no}</p>
              <p><span className="text-slate-500">Supplier:</span> {deleteFor.supplier_name}</p>
              <p>
                <span className="text-slate-500">Date:</span>{" "}
                {dmy(deleteFor.purchase_date || deleteFor.created_at)}
              </p>
              <p><span className="text-slate-500">Total:</span> {inr(deleteFor.total)}</p>
            </div>
            <Field label="Reason for deletion *">
              <textarea
                required
                minLength={3}
                rows={3}
                className={inputCls}
                value={deleteReason}
                data-testid="delete-purchase-reason-input"
                onChange={(event) => setDeleteReason(event.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => setDeleteFor(null)} className={ghostBtnCls}
                data-testid="delete-purchase-cancel-btn">
                Cancel
              </button>
              <button
                disabled={deleteBusy}
                className={[
                  "inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2",
                  "text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60",
                ].join(" ")}
                data-testid="delete-purchase-confirm-btn"
              >
                {deleteBusy ? "Deleting…" : "Delete purchase"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={Boolean(hardDeleteFor)}
        onClose={() => setHardDeleteFor(null)}
        title="Hard delete purchase"
        subtitle={hardDeleteFor ? `${hardDeleteFor.purchase_no} · ${hardDeleteFor.supplier_name}` : ""}
        testid="hard-delete-purchase-modal"
      >
        {hardDeleteFor && (
          <form onSubmit={submitHardDelete} className="space-y-5">
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-950"
              data-testid="hard-delete-purchase-warning">
              <p className="font-semibold">This cannot be undone.</p>
              <p className="mt-1">
                This permanently removes this purchase and its exclusively-dependent Pharmacy
                records, including purchase stock, batches, movements, payments, and test graph.
              </p>
            </div>
            <div className="grid gap-3 rounded-md bg-slate-50 p-4 text-sm sm:grid-cols-2"
              data-testid="hard-delete-purchase-summary">
              <p><span className="text-slate-500">Purchase:</span> {hardDeleteFor.purchase_no}</p>
              <p><span className="text-slate-500">Supplier:</span> {hardDeleteFor.supplier_name}</p>
              <p><span className="text-slate-500">Amount:</span> {inr(hardDeleteFor.total)}</p>
            </div>
            <Field label='Type DELETE to permanently delete *'>
              <input
                required
                className={inputCls}
                value={hardDeleteConfirmation}
                data-testid="hard-delete-purchase-confirmation-input"
                onChange={(event) => setHardDeleteConfirmation(event.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setHardDeleteFor(null)}
                className={ghostBtnCls}
                data-testid="hard-delete-purchase-cancel-btn"
              >
                Cancel
              </button>
              <button
                disabled={hardDeleteBusy}
                className={[
                  "inline-flex items-center justify-center rounded-lg bg-red-800 px-4 py-2",
                  "text-sm font-semibold text-white hover:bg-red-950 disabled:opacity-60",
                ].join(" ")}
                data-testid="hard-delete-purchase-confirm-btn"
              >
                {hardDeleteBusy ? "Permanently deleting…" : "Permanently delete"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Reset Pharmacy Data"
        subtitle="Admin maintenance action"
        testid="reset-pharmacy-data-modal"
      >
        <form onSubmit={submitPharmacyReset} className="space-y-5">
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-950"
            data-testid="reset-pharmacy-data-warning">
            <p className="font-semibold">This permanently removes Pharmacy transactional data.</p>
            <p className="mt-1">
              Purchases, purchase payments and returns, batches, inventory movements, sales,
              B2B invoices and payments, Pharmacy expenses, deleted history, and their Pharmacy
              audit references will be removed for this workspace only.
            </p>
            <p className="mt-1">
              Pharmacy identity/configuration, suppliers, medicines, B2B accounts, users, and all
              Hospital, Lab, Diagnostic, and Maternity records are protected.
            </p>
          </div>
          <Field label="Reason for reset *">
            <textarea
              required
              minLength={3}
              rows={3}
              className={inputCls}
              value={resetReason}
              data-testid="reset-pharmacy-reason-input"
              onChange={(event) => setResetReason(event.target.value)}
            />
          </Field>
          <Field label='Type RESET PHARMACY to permanently reset *'>
            <input
              required
              className={inputCls}
              value={resetConfirmation}
              data-testid="reset-pharmacy-confirmation-input"
              onChange={(event) => setResetConfirmation(event.target.value)}
            />
          </Field>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setResetOpen(false)}
              className={ghostBtnCls}
              data-testid="reset-pharmacy-cancel-btn"
            >
              Cancel
            </button>
            <button
              disabled={resetBusy}
              className={[
                "inline-flex items-center justify-center rounded-lg bg-red-800 px-4 py-2",
                "text-sm font-semibold text-white hover:bg-red-950 disabled:opacity-60",
              ].join(" ")}
              data-testid="reset-pharmacy-confirm-btn"
            >
              {resetBusy ? "Resetting…" : "Reset Pharmacy Data"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={deletedOpen}
        onClose={() => setDeletedOpen(false)}
        wide
        title="Deleted purchases"
        subtitle="Historical records only. Deleted purchases cannot be edited or restored here."
        testid="deleted-purchases-modal"
      >
        {deletedError && <ErrorState message={deletedError} />}
        {!deletedError && !deletedItems ? <Loading /> : (
          <Table
            testid="deleted-purchases-table"
            columns={[
              "Purchase no.",
              "Supplier",
              "Date",
              "Total",
              "Deleted by",
              "Deleted at",
              "Reason",
              "",
            ]}
            rows={deletedItems || []}
            empty={(
              <EmptyState
                title="No deleted purchases"
                subtitle="Deleted purchase history will appear here."
                testid="deleted-purchases-empty"
              />
            )}
            renderRow={(purchase) => (
              <tr
                key={purchase.purchase_id}
                data-testid={`deleted-purchase-row-${purchase.purchase_id}`}
              >
                <Td className="font-mono-num text-slate-900">{purchase.purchase_no}</Td>
                <Td className="font-medium text-slate-900">{purchase.supplier_name}</Td>
                <Td className="whitespace-nowrap text-slate-600">
                  {dmy(purchase.purchase_date || purchase.created_at)}
                </Td>
                <Td className="text-slate-900">{inr(purchase.total)}</Td>
                <Td className="text-slate-600">{purchase.deletion?.deleted_by_name || "—"}</Td>
                <Td className="whitespace-nowrap text-slate-600">
                  {dmyTime(purchase.deletion?.deleted_at)}
                </Td>
                <Td className="max-w-[240px] text-slate-600">{purchase.deletion?.reason || "—"}</Td>
                <Td className="text-right">
                  <button
                    onClick={() => openDeletedDetail(purchase)}
                    className={ghostBtnCls}
                    data-testid={`view-deleted-purchase-${purchase.purchase_id}`}
                  >
                    View
                  </button>
                </Td>
              </tr>
            )}
          />
        )}
      </Modal>

      <Modal open={supOpen} onClose={() => setSupOpen(false)} wide title="Add supplier"
        subtitle="GSTIN and supplier name must be unique within your business." testid="add-supplier-modal">
        <SupplierForm form={supForm} setForm={setSupForm} onSubmit={addSupplier} busy={false}
          error={supError} editing={false} />
      </Modal>

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} wide
        title={detail?.purchase_no || ""} subtitle={detail?.supplier_name} testid="purchase-detail-modal">
        {detail && (
          <div className="space-y-5 text-sm">
            <div className="grid gap-4 sm:grid-cols-3">
              {[["Status", titleize(detail.status || "completed")], ["Supplier invoice", detail.supplier_invoice_no || "—"],
                ["Purchase date", dmy(detail.purchase_date || detail.created_at)],
                ["Due date", detail.due_date ? dmy(detail.due_date) : "—"],
                ["Reference", detail.reference || "—"], ["Total", inr(detail.total)],
                ["Paid", inr(detail.paid_amount || 0)],
                ["Balance", inr(detail.balance_amount ?? detail.total)],
                ["Payment status", titleize(detail.payment_status || "unpaid")],
                ["Tax split", detail.interstate ? `IGST ${inr(detail.igst || 0)}`
                  : `CGST ${inr(detail.cgst || 0)} + SGST ${inr(detail.sgst || 0)}`]].map(([k, v]) => (
                <div key={k}><p className="overline text-slate-400">{k}</p><p className="mt-1.5">{v}</p></div>
              ))}
            </div>
            {detail.duplicate_invoice_override_reason && (
              <p className="rounded-md bg-amber-50 px-4 py-3 text-xs text-amber-800">
                Duplicate invoice override: {detail.duplicate_invoice_override_reason}
              </p>
            )}
            {detail.correction_reason && (
              <p className="rounded-md bg-amber-50 px-4 py-3 text-xs text-amber-800">
                Corrected by {detail.corrected_by_name}: {detail.correction_reason}
              </p>
            )}
            {detail.status === "deleted" && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900"
                data-testid="deleted-purchase-detail">
                <p className="font-semibold">Deleted purchase history</p>
                <p className="mt-1">
                  Deleted by {detail.deletion?.deleted_by_name || "—"} on{" "}
                  {dmyTime(detail.deletion?.deleted_at)}.
                </p>
                <p className="mt-1">Reason: {detail.deletion?.reason || "—"}</p>
                <p className="mt-1">Stock reversal: {(detail.deletion?.stock_reversals || []).length} batch(es), {(
                  detail.deletion?.stock_reversals || []
                ).reduce((sum, reversal) => sum + Number(reversal.quantity || 0), 0)} base unit(s).
                </p>
              </div>
            )}
            {(detail.payments || []).length > 0 && (
              <ul className="space-y-1.5 text-xs text-slate-600">
                {detail.payments.map((p, i) => (
                  <li key={i}>
                    {titleize((p.mode || "").replace(/_/g, " "))} · {inr(p.amount)}
                    {p.reference ? ` · ${p.reference}` : ""}{p.note ? ` · ${p.note}` : ""}
                  </li>
                ))}
              </ul>
            )}
            <ul className="space-y-2">
              {detail.items.map((l, i) => (
                <li key={i} className="rounded-md bg-slate-50 px-4 py-3">
                  <span className="font-medium">{l.medicine_name}</span> · Batch {l.batch_no} · Exp {dmy(l.expiry_date)}
                  {" "}· {l.quantity}{l.free_quantity ? ` + ${l.free_quantity} free` : ""} @ {inr(l.purchase_price)}
                  {" "}· MRP {inr(l.mrp)} · GST {l.gst_percent}% · {inr(l.line_total)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(ret)} onClose={() => setRet(null)} wide title="Purchase return"
        subtitle={ret?.purchase_no} testid="purchase-return-modal">
        {ret && (
          <form onSubmit={submitReturn} className="space-y-5">
            {ret.items.map((l) => (
              <div key={l.batch_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 px-4 py-3 text-sm">
                <span>{l.medicine_name} · Batch {l.batch_no} · purchased {l.quantity}, returned {l.returned || 0}</span>
                <input type="number" min="0" max={l.quantity - (l.returned || 0)} className={`${inputCls} !w-28`}
                  data-testid={`return-qty-${l.batch_id}`} value={retQty[l.batch_id] || ""}
                  onChange={(e) => setRetQty({ ...retQty, [l.batch_id]: e.target.value })} />
              </div>
            ))}
            <Field label="Reason *">
              <input required minLength={3} className={inputCls} value={retReason} data-testid="return-reason-input"
                onChange={(e) => setRetReason(e.target.value)} />
            </Field>
            <button className={primaryBtnCls} data-testid="return-submit-btn">Record return</button>
          </form>
        )}
      </Modal>

      <PrintPreview open={Boolean(print)} onClose={() => setPrint(null)}
        title={`Purchase ${print?.purchase_no || ""}`} subtitle={print?.supplier_name}
        testid="purchase-print-preview">
        {print && (
          <>
            <DocHeader business={business} docTitle={print.status === "draft" ? "Purchase Draft" : "Purchase Entry"}
              right={<>
                <p className="text-[11px]">No: <b>{print.purchase_no}</b></p>
                <p className="text-[11px]">{dmyTime(print.created_at)}</p>
              </>} />
            <Grid rows={[["Supplier", print.supplier_name], ["Supplier invoice", print.supplier_invoice_no || "—"],
              ["Supplier invoice date", print.supplier_invoice_date ? dmy(print.supplier_invoice_date) : "—"],
              ["Purchase date", dmy(print.purchase_date || print.created_at)],
              ["Due date", print.due_date ? dmy(print.due_date) : "—"],
              ["Status", titleize(print.status || "completed")],
              ["Reference", print.reference || "—"], ["Entered by", print.created_by_name || "—"]]} />
            <table className="mt-6 w-full border-collapse text-[11.5px]">
              <thead>
                <tr className="border-y-2 border-slate-800 text-left">
                  <th className="py-1.5 w-6">#</th><th className="py-1.5">Product</th><th className="py-1.5">Batch</th>
                  <th className="py-1.5">Expiry</th><th className="py-1.5 text-right">Qty</th>
                  <th className="py-1.5 text-right">Free</th><th className="py-1.5 text-right">Rate</th>
                  <th className="py-1.5 text-right">MRP</th><th className="py-1.5 text-right">Disc</th>
                  <th className="py-1.5 text-right">GST</th><th className="py-1.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {print.items.map((l, i) => (
                  <tr key={i} className="border-b border-slate-200">
                    <td className="py-1.5">{i + 1}</td>
                    <td className="py-1.5 font-medium">{l.medicine_name}</td>
                    <td className="py-1.5">{l.batch_no}</td>
                    <td className="py-1.5">{dmy(l.expiry_date)}</td>
                    <td className="py-1.5 text-right">{l.quantity}</td>
                    <td className="py-1.5 text-right">{l.free_quantity || 0}</td>
                    <td className="py-1.5 text-right">{inr(l.purchase_price)}</td>
                    <td className="py-1.5 text-right">{inr(l.mrp)}</td>
                    <td className="py-1.5 text-right">{inr(l.discount || 0)}</td>
                    <td className="py-1.5 text-right">{l.gst_percent}%</td>
                    <td className="py-1.5 text-right font-medium">{inr(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex justify-end">
              <div className="w-72 space-y-1 text-[12px]">
                <div className="flex justify-between"><span className="text-slate-600">Taxable</span><span>{inr(print.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Discount</span><span>- {inr(print.discount_total || 0)}</span></div>
                {print.interstate ? (
                  <div className="flex justify-between"><span className="text-slate-600">IGST</span><span>{inr(print.igst || 0)}</span></div>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-slate-600">CGST</span><span>{inr(print.cgst || 0)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">SGST</span><span>{inr(print.sgst || 0)}</span></div>
                  </>
                )}
                <div className="flex justify-between"><span className="text-slate-600">Round off</span><span>{inr(print.round_off || 0)}</span></div>
                <div className="flex justify-between border-y-2 border-slate-800 py-1.5 font-ui text-[14px] font-bold">
                  <span>Grand total</span><span>{inr(print.total)}</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-600">Paid</span><span>{inr(print.paid_amount || 0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Balance</span><span>{inr(print.balance_amount ?? print.total)}</span></div>
              </div>
            </div>
            {print.status === "draft" && (
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Draft — stock has not been updated for this purchase.
              </p>
            )}
          </>
        )}
      </PrintPreview>
    </div>
  );
}
